/**
 * Card audio playback — Edge TTS + Supabase Storage, with SpeechSynthesis fallback on API failure.
 * Uses audioPlaybackGuard to prevent overlapping playback: repeated clicks while playing are ignored.
 */
import type { Card } from "@/types/linguadeck";
import { requestStartPlayback, releasePlayback } from "./audioPlaybackGuard";
import { generateTtsAudio } from "./generateTtsAudio";
import { speakGameWord } from "./speakGameWord";
import { resolveCardSideSpeechLocale, sanitizeTextForSpeech, toSpeechLocale } from "./speechText";

/** Currently playing HTMLAudioElement — used for internal tracking. */
let currentAudio: HTMLAudioElement | null = null;

/** Re-export for UI components that need to show disabled state during playback. */
export { isAudioPlaying } from "./audioPlaybackGuard";

export interface PlayAudioOptions {
  card: Card;
  side: "front" | "back";
  deckLang?: string;
  volume?: number;
  onEnd?: () => void;
  onGenerating?: () => void;
  onError?: (err: Error) => void;
  /** Called with new audio URL after generation. Use to update local card state. */
  onAudioUrlReady?: (url: string) => void;
  /** Called with URL + locale when a fresh TTS asset is generated. */
  onAudioReady?: (payload: { url: string; locale: string; side: "front" | "back" }) => void;
  saveToDb?: boolean;
}

function getApiBase(): string {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

/**
 * Play pre-generated audio from URL using HTMLAudioElement.
 * No SpeechSynthesis. Returns a promise that resolves when playback starts.
 */
function playFromUrl(url: string, volume: number, onEnd?: () => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const audio = new Audio(url);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.onended = () => {
      if (currentAudio === audio) currentAudio = null;
      onEnd?.();
      resolve();
    };
    audio.onerror = (e) => {
      if (currentAudio === audio) currentAudio = null;
      reject(new Error(`Audio playback failed: ${(e.target as HTMLAudioElement)?.error?.message || "unknown"}`));
    };
    currentAudio = audio;
    audio
      .play()
      .then(() => resolve())
      .catch(reject);
  });
}

/** Minimal silent WAV for iOS Safari audio unlock (must play synchronously in user gesture). */
const SILENT_AUDIO_DATA_URL =
  "data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEAQB8AAEAfAAABAAgAAABmYWN0BAAAAAAAAABkYXRhAAAAAA==";

/** Fallback to browser SpeechSynthesis when API or playback fails. Always invokes onEnd when no playback happens. */
function fallbackToBrowserTts(
  text: string,
  lang: string,
  vol: number,
  onEnd?: () => void
): void {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    onEnd?.();
    return;
  }
  try {
    speakGameWord(text, lang, vol, onEnd);
  } catch (e) {
    console.warn("[playAudio] SpeechSynthesis fallback failed:", e);
    onEnd?.();
  }
}

/**
 * Play card audio. Prefers Edge TTS; falls back to SpeechSynthesis on any API/playback failure.
 * a) If audio_url exists: play immediately, fallback to SpeechSynthesis on playback error.
 * b) If not: POST to /api/generate-audio, play result, update DB. On 500/!res.ok: fallback to SpeechSynthesis.
 */
export async function playAudio(options: PlayAudioOptions): Promise<void> {
  if (!requestStartPlayback()) return;
  const release = () => {
    releasePlayback();
    options.onEnd?.();
  };

  const {
    card,
    side,
    deckLang = "en",
    volume = 1,
    onEnd,
    onGenerating,
    onError,
    onAudioUrlReady,
    onAudioReady,
  } = options;

  const text = sanitizeTextForSpeech(side === "front" ? card.front || "" : card.back || "");
  // CRITICAL: Always use explicit card field language. Never detect language from text (would wrongly speak Ukrainian as Russian etc).
  const lang = resolveCardSideSpeechLocale(card, side, deckLang, "en-US");
  if (typeof console !== "undefined" && console.info) {
    console.info("[playAudio] play_audio_effective_locale_used", { side, effectiveLocale: lang, cardId: card.id });
  }
  const cachedUrl = side === "front" ? card.frontAudioUrl : card.backAudioUrl;
  const cachedLocaleRaw = side === "front" ? card.frontAudioLocale : card.backAudioLocale;
  const cachedLocale = toSpeechLocale(cachedLocaleRaw ?? undefined, "");

  if (cachedUrl && cachedLocale && cachedLocale !== lang) {
    console.info("[playAudio] card_audio_locale_mismatch_detected", {
      side,
      effectiveLocale: lang,
      cachedLocale,
      cardId: card.id,
    });
  }

  if (!text) {
    release();
    return;
  }

  const vol = typeof volume === "number" ? Math.max(0, Math.min(1, volume)) : 1;

  const doFallback = (err?: Error) => {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[playAudio] Using browser TTS fallback.", err?.message ?? "");
    }
    onError?.(err ?? new Error("Falling back to browser TTS"));
    currentAudio = null;
    fallbackToBrowserTts(text, lang, vol, release);
  };

  try {
    // a) Cached URL exists — play immediately, fallback on error
    if (cachedUrl && cachedUrl.startsWith("http") && cachedLocale === lang) {
      try {
        await playFromUrl(cachedUrl, vol, release);
      } catch (err) {
        console.warn("[playAudio] Cached URL failed, falling back to browser TTS:", err);
        doFallback();
      }
      return;
    }

    // b) No cached URL — generate via API
    // iOS Safari unlock: create Audio and play silent clip synchronously while user gesture is valid
    const audioEl = new Audio(SILENT_AUDIO_DATA_URL);
    audioEl.volume = vol;
    audioEl.play().catch(() => {});
    currentAudio = audioEl;

    onGenerating?.();
    const base = getApiBase();
    if (!base) {
      console.warn("[playAudio] No API base URL, falling back to browser TTS");
      doFallback();
      return;
    }

    try {
      const { audioUrl } = await generateTtsAudio({
        text,
        locale: lang,
        cardId: card.id,
        side,
      });

      onAudioUrlReady?.(audioUrl);
      onAudioReady?.({ url: audioUrl, locale: lang, side });
      // Play on the same unlocked element — Safari allows it because we activated it synchronously
      audioEl.src = audioUrl;
      audioEl.onended = () => {
        if (currentAudio === audioEl) currentAudio = null;
        release();
      };
      audioEl.onerror = () => {
        if (currentAudio === audioEl) currentAudio = null;
        doFallback(new Error("Playback failed"));
      };
      await audioEl.play();
    } catch (err) {
      // API 500, network error, or playback error — fallback to SpeechSynthesis
      console.warn("[playAudio] API/playback failed, falling back to browser TTS:", err);
      const apiError = err instanceof Error ? err : new Error(String(err));
      doFallback(apiError);
    }
  } catch (err) {
    // Catch-all: prevent unhandled promise rejections
    console.error("[playAudio] Unexpected error, falling back to browser TTS:", err);
    doFallback();
  }
}
